import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Drawer, DrawerContent, DrawerTrigger, DrawerHeader, DrawerTitle } from '../ui/drawer';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import * as E from '../../lib/filterEngine';
import { SlidersHorizontal, ArrowUpDown, X, Bookmark, Link2, Star } from 'lucide-react';

// ---- hook: schema + items -> filtered/sorted view with shareable URL state ----
export function useFilterSort(schema, items) {
  const [sp, setSp] = useSearchParams();
  const facets = useMemo(() => E.buildFacets(items || [], schema), [items, schema]);
  const { values, sortKey } = useMemo(() => E.decodeFromParams(sp, schema, facets), [sp, schema, facets]);
  const visible = useMemo(
    () => E.applySort(E.applyFilters(items || [], schema, values, facets), schema, sortKey),
    [items, schema, values, facets, sortKey]);
  const active = useMemo(() => E.activeCount(schema, values, facets), [schema, values, facets]);
  const commit = (nv, ns) => setSp(E.writeToParams(sp, schema, facets, nv ?? values, ns ?? sortKey), { replace: true });
  return {
    facets, values, sortKey, visible, activeCount: active,
    setValues: (v) => commit(v, sortKey),
    setSort: (s) => commit(values, s),
    clearAll: () => setSp(E.writeToParams(sp, schema, facets, E.defaultValues(schema, facets), schema.defaultSort), { replace: true }),
  };
}

// ---- presentational filter fields (shared by desktop rail + mobile sheet) ----
function FilterFields({ schema, facets, values, onChange, currency }) {
  const setRange = (key, v) => onChange({ ...values, [key]: { min: v[0], max: v[1] } });
  const toggleMulti = (key, opt) => {
    const cur = new Set((values[key] || []).map(String));
    const s = String(opt); cur.has(s) ? cur.delete(s) : cur.add(s);
    onChange({ ...values, [key]: Array.from(cur) });
  };
  return (
    <div className="space-y-6" data-testid="filter-controls">
      {schema.filters.map((f) => {
        const fc = facets[f.key];
        if (!fc?.available) return null;
        if (f.type === 'range') {
          const val = values[f.key] || { min: fc.min, max: fc.max };
          const fmt = f.format || ((x) => x);
          const step = Math.max(1, Math.round((fc.max - fc.min) / 100));
          return (
            <div key={f.key} data-testid={`filter-${f.key}`}>
              <h4 className="font-semibold text-sm mb-3">{f.label}</h4>
              <Slider min={fc.min} max={fc.max} step={step} value={[val.min, val.max]}
                onValueChange={(v) => setRange(f.key, v)} data-testid={`filter-${f.key}-slider`} />
              <p className="mt-2 text-sm text-muted-foreground">{fmt(val.min, currency)} <span className="mx-1">–</span> {fmt(val.max, currency)}</p>
            </div>
          );
        }
        if (f.type === 'multi') {
          return (
            <div key={f.key} data-testid={`filter-${f.key}`}>
              <h4 className="font-semibold text-sm mb-3">{f.label}</h4>
              <div className="space-y-2 max-h-48 overflow-auto pr-1">
                {fc.options.map((opt) => (
                  <label key={String(opt)} className="flex items-center gap-2.5 cursor-pointer" data-testid={`filter-${f.key}-${String(opt).toLowerCase().replace(/\s+/g, '-')}`}>
                    <Checkbox checked={(values[f.key] || []).map(String).includes(String(opt))} onCheckedChange={() => toggleMulti(f.key, opt)} />
                    <span className="text-sm">{f.optionLabel ? f.optionLabel(opt) : String(opt)}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        }
        // toggle
        return (
          <div key={f.key} data-testid={`filter-${f.key}`}>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox checked={!!values[f.key]} onCheckedChange={(v) => onChange({ ...values, [f.key]: !!v })} data-testid={`filter-${f.key}-toggle`} />
              <span className="text-sm font-medium">{f.label}</span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

// ---- the drop-in results block: sort chips + desktop rail + mobile sheet + grid ----
export function FilteredResults({ schema, items, renderItem, gridClassName, testId = 'results', countNoun = 'result', rightRail, onInteract }) {
  const { currency } = useApp();
  const [sp, setSp] = useSearchParams();
  const { facets, values, sortKey, visible, activeCount, setValues, setSort, clearAll } = useFilterSort(schema, items);
  const notify = (fn) => (arg) => { fn(arg); onInteract?.(); };
  const availableSorts = (schema.sorts || []).filter((s) => !s.requiresField || (items || []).some((o) => o[s.requiresField] != null));

  // ---- Saved views (localStorage) + shareable link ----
  const storeKey = `fd_saved_${schema.key}`;
  const [saved, setSaved] = useState(() => { try { return JSON.parse(localStorage.getItem(storeKey) || '[]'); } catch { return []; } });
  const persist = (list) => { setSaved(list); try { localStorage.setItem(storeKey, JSON.stringify(list)); } catch { /* ignore */ } };
  const saveView = () => {
    const name = (window.prompt('Name this view (filters + sort will be saved):') || '').trim();
    if (!name) return;
    const query = sp.toString();
    const list = [...saved.filter((v) => v.name !== name), { name, query }];
    persist(list);
    toast.success(`Saved view “${name}”`);
  };
  const applyView = (v) => { setSp(new URLSearchParams(v.query), { replace: false }); onInteract?.(); };
  const removeView = (name) => persist(saved.filter((v) => v.name !== name));
  const copyLink = () => {
    try { navigator.clipboard.writeText(window.location.href); toast.success('Shareable link copied'); }
    catch { toast.message(window.location.href); }
  };

  const SavedBar = (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="saved-views">
      {saved.map((v) => (
        <span key={v.name} className="inline-flex items-center gap-1 rounded-full border border-border bg-card pl-2.5 pr-1 py-1 text-xs" data-testid={`saved-view-${v.name.toLowerCase().replace(/\s+/g, '-')}`}>
          <button onClick={() => applyView(v)} className="inline-flex items-center gap-1 hover:text-accent"><Star className="h-3 w-3" />{v.name}</button>
          <button onClick={() => removeView(v.name)} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
        </span>
      ))}
      <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" onClick={saveView} data-testid="save-view"><Bookmark className="h-3.5 w-3.5 mr-1" />Save view</Button>
      <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" onClick={copyLink} data-testid="copy-link"><Link2 className="h-3.5 w-3.5 mr-1" />Copy link</Button>
    </div>
  );

  const Sort = (
    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="sort-chips">
      <ArrowUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
      {availableSorts.map((s) => (
        <button key={s.key} onClick={() => notify(setSort)(s.key)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${sortKey === s.key ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`}
          data-testid={`sort-${s.key}`}>{s.label}</button>
      ))}
    </div>
  );

  const filtersInner = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Filters {activeCount > 0 && <Badge className="rounded-full bg-accent text-accent-foreground text-[10px]" data-testid="active-filter-count">{activeCount}</Badge>}</h3>
        {activeCount > 0 && <button onClick={() => notify(clearAll)()} className="text-xs text-accent hover:underline inline-flex items-center gap-1" data-testid="clear-filters"><X className="h-3 w-3" />Clear all</button>}
      </div>
      <FilterFields schema={schema} facets={facets} values={values} onChange={notify(setValues)} currency={currency} />
    </>
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4">
        {Sort}
        <Drawer>
          <DrawerTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-xl lg:hidden shrink-0" data-testid="open-filters-button">
              <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters {activeCount > 0 && <span className="ml-1 rounded-full bg-accent text-accent-foreground text-[10px] px-1.5">{activeCount}</span>}
            </Button>
          </DrawerTrigger>
          <DrawerContent data-testid="filters-bottom-sheet">
            <DrawerHeader><DrawerTitle>Filters & sort</DrawerTitle></DrawerHeader>
            <div className="px-4 pb-8 overflow-auto max-h-[70vh]">
              <div className="mb-4">{Sort}</div>
              {filtersInner}
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      <div className="mb-3">{SavedBar}</div>

      <p className="text-sm text-muted-foreground mb-3" data-testid={`${testId}-count`}>
        <span className="font-semibold text-foreground">{visible.length}</span> {countNoun}{visible.length === 1 ? '' : 's'}{items ? ` of ${items.length}` : ''}
      </p>

      <div className={`grid gap-6 ${rightRail ? 'lg:grid-cols-[240px_1fr_300px]' : 'lg:grid-cols-[240px_1fr]'}`}>
        <aside className="hidden lg:block">
          <div className="sticky top-20 rounded-2xl border border-border bg-card p-5">{filtersInner}</div>
        </aside>
        <div className="min-w-0">
          {visible.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-border" data-testid={`${testId}-empty`}>
              <p className="font-medium">No {countNoun}s match your filters</p>
              <button onClick={() => notify(clearAll)()} className="text-sm text-accent hover:underline mt-1">Clear all filters</button>
            </div>
          ) : (
            <div className={gridClassName || 'space-y-3'} data-testid={`${testId}-list`}>
              {visible.map((it) => renderItem(it))}
            </div>
          )}
        </div>
        {rightRail && <aside className="lg:block"><div className="sticky top-20">{rightRail}</div></aside>}
      </div>
    </div>
  );
}
